// useDirectory — resolves auth user_ids to people (name/email/avatar) and powers
// the people-picker. Conversations key on the auth user_id; the employee directory
// carries user_id alongside names, so we build a user_id → person map from it.
//
// Loaded once and cached at module level (shared across every conversations view),
// so name resolution and the picker share one fetch. For large tenants this should
// move to server-side search + targeted lookups; fine for now.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAllEmployees, type Employee } from '@/lib/hrApi';
import { DANA_BOT_USER_ID } from '../types';

export interface Person {
  userId: string;
  employeeId?: string; // employee master id — for assigning work
  name: string;
  email?: string;
  avatarUrl?: string;
  position?: string;
}

function toPerson(e: Employee): Person | null {
  if (!e.user_id) return null; // only users with a login can be messaged
  const name = `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email || e.user_id.slice(0, 8);
  return {
    userId: e.user_id,
    employeeId: e.id ? String(e.id) : undefined,
    name,
    email: e.email ?? undefined,
    avatarUrl: e.profile_picture_url ?? undefined,
    position: e.position ?? undefined,
  };
}

/** An employee with no login: they exist in the company, but a mention could never
 *  reach them. The picker lists them greyed out with the reason rather than showing
 *  nothing, which looked like @mentions were broken. */
export interface UnlinkedPerson { name: string; position?: string }

interface Loaded { people: Person[]; unlinked: UnlinkedPerson[] }

let cache: Loaded | null = null;
let inflight: Promise<Loaded> | null = null;

async function loadPeople(): Promise<Loaded> {
  if (cache) return cache;
  if (!inflight) {
    inflight = getAllEmployees()
      .then((list) => {
        cache = {
          people: list.map(toPerson).filter((p): p is Person => !!p),
          unlinked: list
            .filter((e) => !e.user_id && String(e.status ?? '').toLowerCase() !== 'terminated')
            .map((e) => ({ name: `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email || '', position: e.position ?? undefined }))
            .filter((u) => !!u.name),
        };
        inflight = null;
        return cache;
      })
      .catch((e) => {
        inflight = null;
        throw e;
      });
  }
  return inflight;
}

export interface Directory {
  people: Person[];
  loaded: boolean;
  /** Resolve a user_id to a display name (Dana + self handled; falls back to a short id). */
  nameOf: (userId?: string) => string;
  personOf: (userId?: string) => Person | undefined;
  /** Client-side name/email search for the picker. */
  search: (query: string) => Person[];
  /** Colleagues matching the query who have no login — shown disabled, with the reason. */
  searchUnlinked: (query: string) => UnlinkedPerson[];
}

export function useDirectory(): Directory {
  const [people, setPeople] = useState<Person[]>(cache?.people ?? []);
  const [unlinked, setUnlinked] = useState<UnlinkedPerson[]>(cache?.unlinked ?? []);
  const [loaded, setLoaded] = useState<boolean>(!!cache);

  useEffect(() => {
    let cancelled = false;
    loadPeople()
      .then((p) => {
        if (!cancelled) {
          setPeople(p.people);
          setUnlinked(p.unlinked);
          setLoaded(true);
        }
      })
      .catch(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const byId = useMemo(() => {
    const m = new Map<string, Person>();
    people.forEach((p) => m.set(p.userId, p));
    return m;
  }, [people]);

  const personOf = useCallback((userId?: string) => (userId ? byId.get(userId) : undefined), [byId]);

  const nameOf = useCallback(
    (userId?: string) => {
      if (!userId) return '';
      if (userId === DANA_BOT_USER_ID) return 'Dana';
      return byId.get(userId)?.name ?? userId.slice(0, 8);
    },
    [byId],
  );

  const search = useCallback(
    (query: string) => {
      const s = query.trim().toLowerCase();
      if (!s) return people;
      return people.filter(
        (p) => p.name.toLowerCase().includes(s) || (p.email ?? '').toLowerCase().includes(s),
      );
    },
    [people],
  );

  const searchUnlinked = useCallback(
    (query: string) => {
      const s = query.trim().toLowerCase();
      if (!s) return []; // only surfaced once someone types a name
      return unlinked.filter((p) => p.name.toLowerCase().includes(s));
    },
    [unlinked],
  );

  return { people, loaded, nameOf, personOf, search, searchUnlinked };
}
